// BUILD-042 (Oct 16) — HR's review of the fields found in an existing fillable PDF.
//
// Nothing is guessed: every field is listed with what happened to it. A field the signing provider cannot carry (unsupported) that the PDF
// REQUIRES, or that is calculated, can only be resolved by placing a real field over its original spot — saying "not needed" is not offered.
// Other unsupported items may be replaced or confirmed unnecessary. The server repeats this check before publishing.
import type { HrImportReport, HrImportResolution } from '../../api/client';
import type { UnresolvedItem } from './formFieldsModel';

const STATUS_STYLE: Record<string, string> = {
  imported: 'text-emerald-700', 'imported-with-note': 'text-amber-700', unsupported: 'text-red-700',
};
const STATUS_LABEL: Record<string, string> = { imported: 'Imported', 'imported-with-note': 'Imported — please check', unsupported: 'Cannot be imported' };

export function ImportReview({
  report, importedKeys, resolutions, unresolved, busy, hasUnimported, onImportAll, onReplace, onAck, onGoTo,
}: {
  report: HrImportReport;
  /** acroNames currently present as fields in the editor. */
  importedKeys: Set<string>;
  resolutions: Record<string, HrImportResolution>;
  unresolved: UnresolvedItem[];
  busy: boolean;
  /** At least one importable field is not in the editor yet. */
  hasUnimported: boolean;
  onImportAll: () => void;
  onReplace: (itemId: string) => void;
  onAck: (itemId: string) => void;
  onGoTo: (page: number) => void;
}) {
  const unresolvedIds = new Set(unresolved.map((u) => u.itemId));
  return (
    <div className="space-y-2 text-xs text-gray-700" data-testid="import-review">
      <div className="font-semibold text-gray-800">Fields found in this PDF</div>
      <p className="text-gray-600">
        {report.acroFieldCount} fillable field{report.acroFieldCount === 1 ? '' : 's'} found: {report.counts.imported} imported as they are, {report.counts.importedWithNote} imported with a note, {report.counts.unsupported} that cannot be imported.
        Review each one, then add anything missing by hand.
      </p>
      {report.hybridNote && <p className="rounded border border-sky-200 bg-sky-50 p-1.5 text-sky-900" data-testid="hybrid-note">{report.hybridNote}</p>}
      <button type="button" onClick={onImportAll} disabled={busy || !hasUnimported} className="px-2.5 py-1.5 rounded bg-[#4AABDE] text-white font-medium disabled:opacity-40" data-testid="import-all">
        {hasUnimported ? 'Import the fields from this PDF' : 'All importable fields are in the editor'}
      </button>
      <ul className="space-y-1.5" data-testid="import-items">
        {report.items.map((it) => {
          const res = resolutions[it.id];
          const open = unresolvedIds.has(it.id);
          return (
            <li key={it.id} className={`rounded border p-1.5 ${open ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`} data-testid={`import-item-${it.id}`}>
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-medium break-all">{it.acroName}</div>
                  <div className={STATUS_STYLE[it.status]}>
                    {STATUS_LABEL[it.status]}{it.requiredInPdf ? ' · required in the PDF' : ''}{it.calculated ? ' · calculated' : ''}
                    {it.status !== 'unsupported' && !importedKeys.has(it.acroName) ? ' · not in the editor' : ''}
                  </div>
                </div>
                {it.page ? <button type="button" className="underline text-[#1d6f9f] shrink-0" onClick={() => onGoTo(it.page!)}>page {it.page}</button> : null}
              </div>
              {it.notes.length > 0 && <ul className="list-disc ml-4 text-gray-600 mt-0.5">{it.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>}
              {it.status === 'unsupported' && (
                <div className="mt-1 flex gap-3 flex-wrap items-center">
                  {res?.kind === 'replaced' ? <span className="text-emerald-700">Replaced by "{res.fieldKey}"</span> : null}
                  {res?.kind === 'ack' ? <span className="text-gray-600">Confirmed not needed</span> : null}
                  {it.box && it.page && <button type="button" className="underline text-[#1d6f9f]" onClick={() => onReplace(it.id)} data-testid={`replace-${it.id}`}>Place a replacement text box over it</button>}
                  {!it.blocking && <button type="button" className="underline text-gray-600" onClick={() => onAck(it.id)} data-testid={`ack-${it.id}`}>It is not needed</button>}
                </div>
              )}
              {open && <div className="mt-1 text-red-800" role="alert">{unresolved.find((u) => u.itemId === it.id)?.message}</div>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
